import { Link } from 'react-router';
import { Empty, Loading, PageHeader } from '../components/ui';
import { useI18n } from '../i18n';
import { useAllOwnership, useArrears, useFiscalYears } from '../ledger-queries';
import { useBuildings, useCurrentQuoteParts, useParties, useUnits } from '../queries';
import { useSyndicate } from '../session';

/** All units of the syndicate; grouped by building only when there is more than one. */
export function UnitsPage() {
  const { t, number, money } = useI18n();
  const s = useSyndicate();
  const units = useUnits();
  const buildings = useBuildings();
  const quoteParts = useCurrentQuoteParts();
  const ownership = useAllOwnership();
  const parties = useParties();
  const years = useFiscalYears();
  const showBalance = s.can('ledger.read') && !!years.data?.length;
  const arrears = useArrears(showBalance);

  if (units.isPending || buildings.isPending) return <Loading label={t.loading} />;
  const groups = (buildings.data ?? []).map((b) => ({ building: b, units: (units.data ?? []).filter((u) => u.buildingId === b.id) }));
  const showOwners = s.can('registry.contacts.read');
  const owners = (unitId: string) =>
    ownership.data
      ?.filter((o) => o.unitId === unitId)
      .map((o) => parties.data?.find((p) => p.id === o.partyId)?.displayName)
      .join(', ') ?? '';
  const share = (unitId: string) => {
    const part = quoteParts.data?.parts.find((p) => p.unitId === unitId);
    return part && quoteParts.data ? `${number((part.numerator / quoteParts.data.denominator) * 100, 3)} %` : '—';
  };

  return (
    <>
      <PageHeader title={t.unitsPage.title} />
      {units.data?.length === 0 && <Empty>{t.unitsPage.empty}</Empty>}
      {groups
        .filter((g) => g.units.length > 0)
        .map((g) => (
          <section key={g.building.id}>
            {groups.length > 1 && <h2>{g.building.name}</h2>}
            <table className="table table-stack">
              <thead>
                <tr>
                  <th>{t.units.label}</th>
                  <th>{t.units.kind}</th>
                  <th className="num">{t.units.area}</th>
                  <th className="num">{t.unitsPage.quotePart}</th>
                  {showOwners && <th>{t.unitsPage.owners}</th>}
                  {showBalance && <th className="num">{t.unitsPage.balance}</th>}
                </tr>
              </thead>
              <tbody>
                {g.units.map((u) => {
                  const balance = arrears.data?.find((r) => r.unitId === u.id)?.balanceCents ?? 0;
                  return (
                    <tr key={u.id}>
                      <td className="cell-title">
                        <Link to={`${s.base}/units/${u.id}`}>{u.label}</Link>
                      </td>
                      <td data-label={t.units.kind}>{t.units.kinds[u.kind]}</td>
                      <td className="num" data-label={t.units.area}>{u.areaM2 === null ? '—' : number(u.areaM2, 2)}</td>
                      <td className="num" data-label={t.unitsPage.quotePart}>{share(u.id)}</td>
                      {showOwners && <td data-label={t.unitsPage.owners}>{owners(u.id)}</td>}
                      {showBalance && (
                        <td className={`num${balance > 0 ? ' amount-out' : ''}`} data-label={t.unitsPage.balance}>
                          {money(balance)}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        ))}
    </>
  );
}
